import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Close as CloseIcon } from '@mui/icons-material';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { constants } from '../constants';
import { UserAPI } from '../apis/UserAPI';
import { ConfigContext } from './ConfigContext';
const RoleModal = ({ open, onClose, onSave, role, loading, applications = [] }) => {
    const [roleName, setRoleName] = useState('');
    const [selectedPolicies, setSelectedPolicies] = useState([]);
    const [availablePolicies, setAvailablePolicies] = useState([]);
    const [policyLoading, setPolicyLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedAppIds, setSelectedAppIds] = useState([]);
    const { configState } = React.useContext(ConfigContext);
    const isEditMode = !!role;
    // Load available policies when modal opens
    useEffect(() => {
        if (open) {
            loadPolicies();
        }
    }, [open]);
    const loadPolicies = async () => {
        setPolicyLoading(true);
        setError(null);
        try {
            const response = await UserAPI.getPolicies();
            setAvailablePolicies(response.resource);
        }
        catch (err) {
            console.error('Failed to load policies:', err);
            setError(constants.role_modal_error_policies_load_failed);
        }
        finally {
            setPolicyLoading(false);
        }
    };
    useEffect(() => {
        if (role) {
            setRoleName(role.name || role.role_name || '');
            setSelectedPolicies(role.policies || []);
            // Normalize application_id to string[] for default selection
            if (Array.isArray(role.application_id)) {
                const nonNullIds = role.application_id.filter((id) => id && id !== 'null');
                setSelectedAppIds(nonNullIds.length > 0 ? nonNullIds.map(String) : ['none']);
            }
            else if (role.application_id) {
                // If comma-separated string, split, else wrap as array
                if (typeof role.application_id === 'string' &&
                    role.application_id.includes(',')) {
                    setSelectedAppIds(role.application_id
                        .split(',')
                        .map((id) => id.trim()));
                }
                else {
                    setSelectedAppIds([String(role.application_id)]);
                }
            }
            else {
                setSelectedAppIds(['none']);
            }
        }
        else {
            setRoleName('');
            setSelectedPolicies([]);
            setSelectedAppIds([]);
        }
        setError(null);
    }, [role, open]);
    const handleAppChange = (event) => {
        const value = event.target.value;
        const newValue = typeof value === 'string' ? value.split(',') : value;
        // If 'none' is selected, clear other selections; if other is selected, remove 'none'
        if (newValue.includes('none') && !selectedAppIds.includes('none')) {
            setSelectedAppIds(['none']);
        }
        else if (newValue.includes('none') && newValue.length > 1) {
            setSelectedAppIds(newValue.filter((id) => id !== 'none'));
        }
        else {
            setSelectedAppIds(newValue);
        }
    };
    const handlePolicyChange = (event) => {
        const value = event.target.value;
        setSelectedPolicies(typeof value === 'string' ? value.split(',') : value);
    };
    const handleRemovePolicy = (policyToRemove) => {
        setSelectedPolicies((prev) => prev.filter((policy) => policy !== policyToRemove));
    };
    const handleSave = () => {
        if (roleName.trim() && selectedAppIds.length > 0) {
            let appIdsArray;
            if (selectedAppIds.includes('none')) {
                // If 'none' is selected, pass empty array to indicate no application
                appIdsArray = [];
            }
            else if (Array.isArray(selectedAppIds)) {
                appIdsArray = selectedAppIds;
            }
            else if (typeof selectedAppIds === 'string') {
                appIdsArray = [selectedAppIds];
            }
            else {
                appIdsArray = [];
            }
            const roleData = {
                name: roleName.trim(),
                role_name: roleName.trim(),
                role_key: roleName.trim().toLowerCase().replaceAll(/\s+/g, '_'),
                application_id: appIdsArray,
                policies: selectedPolicies,
                peopleAssigned: role?.peopleAssigned ?? 0,
                dateAdded: role?.dateAdded ?? new Date().toLocaleDateString()
            };
            onSave(roleData);
        }
    };
    const canSave = roleName.trim() && selectedAppIds.length > 0;
    const getButtonText = () => {
        if (loading)
            return constants.saving;
        return isEditMode
            ? constants.configuration_modal_button_update
            : constants.configuration_modal_button_add;
    };
    return (_jsxs(Dialog, { open: open, onClose: onClose, maxWidth: "sm", fullWidth: true, sx: {
            '& .MuiDialog-paper': {
                borderRadius: 2,
                boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
                minHeight: 400
            }
        }, children: [_jsx(DialogTitle, { variant: "h6", sx: {
                    color: '#212121',
                    borderBottom: '1px solid #ebebeb'
                }, children: isEditMode
                    ? constants.role_modal_title_edit(configState?.settings?.workspace_name ??
                        constants.workspace_default_name)
                    : constants.role_modal_title_add(configState?.settings?.workspace_name ??
                        constants.workspace_default_name) }), _jsx(DialogContent, { sx: { p: '24px !important' }, children: _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsxs(FormControl, { fullWidth: true, size: "small", sx: { mb: 1 }, children: [_jsx(InputLabel, { children: constants.policy_mgmt_application_label }), _jsxs(Select, { multiple: true, value: selectedAppIds, onChange: handleAppChange, label: constants.policy_mgmt_application_label, renderValue: (selected) => {
                                        if (selected.includes('none')) {
                                            return 'Global Roles';
                                        }
                                        return applications
                                            .filter((app) => selected.includes(String(app.id)))
                                            .map((app) => app.name)
                                            .join(', ');
                                    }, children: [_jsx(MenuItem, { value: "none", children: constants.global_roles }), applications.map((app) => (_jsx(MenuItem, { value: String(app.id), children: app.name }, app.id)))] })] }), _jsx(TextField, { fullWidth: true, size: "small", label: constants.role_modal_field_role_name, value: roleName, onChange: (e) => setRoleName(e.target.value), required: true }), policyLoading ? (_jsxs(Box, { sx: {
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                py: 2
                            }, children: [_jsx(CircularProgress, { size: 20 }), _jsx(Typography, { variant: "body2", children: constants.role_modal_loading_policies })] })) : (_jsxs(FormControl, { fullWidth: true, size: "small", children: [_jsx(InputLabel, { children: constants.role_modal_policies_optional }), _jsx(Select, { multiple: true, value: selectedPolicies, onChange: handlePolicyChange, label: constants.role_modal_policies_optional, renderValue: () => constants.role_modal_select, disabled: availablePolicies.length === 0, children: availablePolicies.map((policy) => (_jsx(MenuItem, { value: policy.name, children: policy.name }, policy.id))) })] })), error && (_jsx(Alert, { severity: "error", sx: { mt: 1 }, children: error })), selectedPolicies.length > 0 && (_jsx(Box, { sx: { display: 'flex', flexWrap: 'wrap', gap: 1 }, children: selectedPolicies.map((policy) => (_jsx(Chip, { label: policy, onDelete: () => handleRemovePolicy(policy), deleteIcon: _jsx(CloseIcon, {}), sx: {
                                    backgroundColor: '#f3f8eb',
                                    color: '#1a5d09',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    height: 24,
                                    '& .MuiChip-deleteIcon': {
                                        color: '#1a5d09',
                                        fontSize: 16,
                                        '&:hover': {
                                            color: '#26890d'
                                        }
                                    }
                                } }, policy))) }))] }) }), _jsxs(DialogActions, { sx: {
                    px: 3,
                    pb: 2,
                    pt: 1,
                    gap: 2,
                    borderTop: '1px solid #ebebeb'
                }, children: [_jsx(Button, { onClick: onClose, disabled: loading, sx: {
                            color: '#26890d',
                            textTransform: 'none',
                            fontFamily: 'Open Sans',
                            fontWeight: 600,
                            fontSize: 16,
                            px: 2,
                            py: 1,
                            '&:hover': {
                                backgroundColor: 'rgba(38, 137, 13, 0.04)'
                            }
                        }, children: constants.cancel }), _jsx(Button, { onClick: handleSave, variant: "contained", disabled: loading || !canSave, sx: {
                            backgroundColor: canSave ? '#26890d' : '#e0e0e0',
                            color: canSave ? '#ffffff' : '#999',
                            textTransform: 'none',
                            fontFamily: 'Open Sans',
                            fontWeight: 600,
                            fontSize: 16,
                            px: 3,
                            py: 1,
                            '&:hover': {
                                backgroundColor: canSave ? '#1f6b0a' : '#e0e0e0'
                            },
                            '&:disabled': {
                                backgroundColor: '#e0e0e0',
                                color: '#999'
                            }
                        }, children: getButtonText() })] })] }));
};
export default RoleModal;
